/* global module, require */
const Style = require('./Style'),
	THREE = require('three'),
	createDocument = require('../createDocument'),
	firstOption = require('../firstOption'),
	{
		getGlassThickness,
		getGlassType,
		glassCT,
		glassCTL,
		glassDefault,
		makeGlass,
	} = require('../data/glass'),
	img = require('../../res/planner-glass-button.png'),
	makeSlottedTube = require('../func/makeSlottedTube'),
	glassHeightImg = require('../../res/diagram-1.png'),
	glassThicknessImg = require('../../res/diagram-2.png'),
	standOffImg = require('../../res/diagram-3.png'),
	floorThicknessImg = require('../../res/diagram-4.png'),
	depthImg = require('../../res/diagram-5.png'),
	steel = require('../mat/steel'),
	unit = require('../unit');

/* unknown */
const gButtonOffset = unit.mm(50),
	gButtonSize = unit.mm(50) / 2,
	gGlassGap = unit.mm(10),
	gInset = unit.mm(160),
	gMaxGlassLength = unit.m(1);

const gGlassThicknesses = {
	[glassCT]: {
		'8mm': 8,
		'10mm': 10,
		'12mm': 12,
		'15mm': 15,
		'19mm': 19,
		'20mm': 20,
	},
	[glassCTL]: {
		'8.76mm': 8.76,
		'10.76mm': 10.76,
		'12.76mm': 12.76,
		'13.5mm': 13.5,
		'17.5mm': 17.5,
		'21.5mm': 21.5,
	},
};

function GlassButton(planner, args) {
	this.args = args;
	this.planner = planner;
	this.name = 'Glass Buttons';
	this.image = img;
	Style.apply(this);
}
GlassButton.prototype = new Style();

GlassButton.prototype.getConfig = function() {
	return {
		installation: {
			name: 'Installation Type',
			options: {
				Inside: 'Inside',
				Outside: 'Outside',
			},
			info: 'System can be used for internal, external and coastal.',
		},
		glassHeight: {
			name: 'Glass Height',
			range: { min: 1100, max: 1200 },
			suffix: 'mm',
			info: createDocument(null, [
				{ _tag: 'img', src: glassHeightImg },
				'1. Please ensure that a minimum height of 1100mm above the walking surface is achieved - with or without a top rail.',
			]),
		},
		glassType: getGlassType(
			createDocument(null, [
				'The Glazing panels can be supplied in two forms.',
				{ _tag: 'h4', innerText: 'Toughened Glazing' },
				'Known as tempered glass this can be upto 5x stronger than normal float glass, a single panel of glass.',
				'Our standard thicknesses offered are 8.0mm, 10.0mm, 12.0mm, 15.0mm, 19.0mm and 20.0mm and are chosen as a result of differences between post optimisation.',
				{ _tag: 'h4', innerText: 'Toughened Laminate Glazing' },
				'Laminated glass has the same strength as regular glass but is fundamentally 2 panels with a plastic interlayer. The interlayer acts as a resin which holds the shards in place should the glass break.',
				'Our standard thicknesses are 8.76mm, 10.76mm, 12.76mm, 13.5mm, 17.5mm and 21.5mm.',
			])
		),
		glassThickness: {
			...getGlassThickness(this.options.glassType, gGlassThicknesses),
			info: createDocument(null, [
				{ _tag: 'img', src: glassThicknessImg },
				'2. Subject to building control, 8mm to 21.5mm',
			]),
		},
		standOff: {
			name: 'Stand Off Dimension',
			options: {
				'10mm': 10,
				'20mm': 20,
				'30mm': 30,
				'40mm': 40,
				'50mm': 50,
			},
			transform: parseFloat,
			info: createDocument(null, [
				{ _tag: 'img', src: standOffImg },
				'3. Distance from face to glass in increments of 10mm.',
			]),
		},
		floorThickness: {
			name: 'Floor Thickness',
			range: { min: 10, max: 50 },
			suffix: 'mm',
			info: createDocument(null, [
				{ _tag: 'img', src: floorThicknessImg },
				'4. Walking surface thickness.',
			]),
		},
		beamThickness: {
			name: 'Underfloor Depth',
			range: { min: 200, max: 300 },
			suffix: 'mm',
			info: createDocument(null, [
				{ _tag: 'img', src: depthImg },
				'5. Depth of support steelwork / brick to block / timber.',
			]),
		},
		handRail: {
			name: 'Hand Rail',
			options: { None: 0, '42.4mm⌀ Slotted': 42.4, '48.3mm⌀ Slotted': 48.3 },
			transform: parseFloat,
		},
	};
};

GlassButton.prototype.setOptions = function(options) {
	const oldGlassType = this.options.glassType;
	var configChange = false;

	this.options = {
		installation: 'Inside',
		glassHeight: 1100,
		glassType: glassDefault,
		glassThickness: 8,
		standOff: 20,
		floorThickness: 25,
		beamThickness: 200,
		handRail: 42.4,
		...options,
	};

	if (oldGlassType !== this.options.glassType) {
		this.options.glassThickness = firstOption(
			gGlassThicknesses[this.options.glassType]
		);
		configChange = true;
	}

	return configChange;
};

GlassButton.prototype.connect = function(a, b, i) {
	const beamThickness = unit.mm(this.options.beamThickness),
		standOff = unit.mm(this.options.standOff),
		floorThickness = unit.mm(this.options.floorThickness),
		glassHeight = unit.mm(this.options.glassHeight),
		glassThickness = unit.mm(this.options.glassThickness),
		glassType = this.options.glassType,
		group = new THREE.Group(),
		handRail = unit.mm(this.options.handRail);

	const between = new THREE.Vector3().subVectors(b, a),
		firstOffset = -floorThickness - gButtonOffset,
		secondOffset = -floorThickness - beamThickness + gButtonOffset,
		sectionCount = Math.ceil(between.length() / gMaxGlassLength),
		sectionVec = new THREE.Vector3().copy(between).divideScalar(sectionCount);

	var position = new THREE.Vector3().copy(a);

	for (var section = 0; section < sectionCount; section++) {
		const next = new THREE.Vector3().copy(position).add(sectionVec);

		group.add(
			makeGlass(glassType, {
				between: sectionVec,
				gap: gGlassGap * 2,
				height: glassHeight + beamThickness + floorThickness,
				offset: beamThickness + floorThickness,
				start: position,
				thickness: glassThickness,
			})
		);

		if (handRail)
			group.add(
				makeSlottedTube({
					start: position,
					between: sectionVec,
					diameter: handRail,
					height: glassHeight,
				})
			);

		group.add(
			makeButton({
				start: position,
				between: sectionVec,
				inset: gInset,
				height: firstOffset,
				size: gButtonSize,
				thickness: standOff,
			})
		);

		group.add(
			makeButton({
				start: position,
				between: sectionVec,
				inset: gInset,
				height: secondOffset,
				size: gButtonSize,
				thickness: standOff,
			})
		);

		group.add(
			makeButton({
				start: next,
				between: sectionVec,
				inset: -gInset,
				height: firstOffset,
				size: gButtonSize,
				thickness: standOff,
			})
		);

		group.add(
			makeButton({
				start: next,
				between: sectionVec,
				inset: -gInset,
				height: secondOffset,
				size: gButtonSize,
				thickness: standOff,
			})
		);

		position = next;
	}

	group.add(this.planner.call('makeSectionLabel', a, b, i - 1));
	this.group.add(group);
};

function makeButton({ start, between, inset, height, size, thickness }) {
	const geo = new THREE.CylinderBufferGeometry(size, size, thickness);
	const offset = new THREE.Vector3()
		.copy(between)
		.normalize()
		.multiplyScalar(inset);

	const mesh = new THREE.Mesh(geo, steel);
	mesh.rotation.x = Math.PI / 2;
	mesh.rotation.z = Math.PI / 2 + Math.atan2(between.x, between.z);

	mesh.position.copy(start).add(offset);
	mesh.position.y = height;

	return mesh;
}

module.exports = GlassButton;
